import { useState, type ReactNode } from "react";
import { createDefaultLoomPrimitiveNode, type LoomAuthoringCommand,
  type LoomAuthoringDocument, type LoomPrimitiveDefinition } from "./loomAuthoringModel";
import type { LoomCommandAcceptance } from "./loomAuthoringHistory";
import { createLoomCommandId } from "./loomAuthoringHistory";
import { LoomProductTree, type LoomProductSectionDesign,
  type LoomProductSelection } from "./LoomProductTree";

interface LoomTaskTreePanelProps {
  readonly advancedAuthoring?: ReactNode;
  readonly aircraftName: string;
  readonly document: LoomAuthoringDocument;
  readonly onCommand: (command: LoomAuthoringCommand, acceptance?: LoomCommandAcceptance) => string | null;
  readonly onOpenSection: (nodeId: string, sectionId: string) => void;
  readonly onSelect: (nodeId: string) => void;
  readonly onSelectControl: (nodeId: string, controlSurfaceId: string) => void;
  readonly onSelectFairing?: (nodeId: string, transitionId: string) => void;
  readonly selection: LoomProductSelection;
  readonly sectionDesigns: readonly LoomProductSectionDesign[];
}

function availableId(document: LoomAuthoringDocument, prefix: string): string {
  let suffix = 1;
  while (document.nodes.some((node) => node.id === `${prefix}-${suffix}`)) suffix += 1;
  return `${prefix}-${suffix}`;
}

export function LoomTaskTreePanel({
  advancedAuthoring, aircraftName, document, onCommand, onOpenSection, onSelect, onSelectControl,
  onSelectFairing,
  sectionDesigns, selection,
}: LoomTaskTreePanelProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const add = (primitive: LoomPrimitiveDefinition["primitive"], prefix: string, label: string) => {
    const id = availableId(document, prefix);
    const command: LoomAuthoringCommand = {
      schema: "loom-authoring-command/v1",
      commandId: createLoomCommandId(`task-add-${prefix}`, document.revision),
      expectedRevision: document.revision,
      kind: "node.attach",
      node: { ...createDefaultLoomPrimitiveNode(id, primitive), label },
      attachment: { kind: "new-root" },
    };
    if (!onCommand(command, { origin: "gui.scene", subjectIds: [id] })) onSelect(id);
  };
  return <aside aria-label="Aircraft tree" className="loom-native-task-tree" data-testid="loom-task-tree-panel">
    <LoomProductTree aircraftName={aircraftName} document={document}
      onOpenSection={onOpenSection} onSelect={onSelect} onSelectControl={onSelectControl}
      onSelectFairing={onSelectFairing}
      sectionDesigns={sectionDesigns} selection={selection} />
    <div aria-label="Add component" className="loom-task-add">
      <span>Add component</span>
      <button onClick={() => add("wing", "wing", "New wing")} type="button">Wing</button>
      <button onClick={() => add("fuselage", "body", "New body")} type="button">Body</button>
      <button onClick={() => add("pod", "propulsion", "New propulsion module")} type="button">Propulsion</button>
    </div>
    {advancedAuthoring ? <details className="loom-task-advanced-authoring"
      data-testid="loom-advanced-authoring" open={advancedOpen}>
      <summary onClick={(event) => {
        event.preventDefault();
        setAdvancedOpen((current) => !current);
      }}>Advanced authoring</summary>
      {advancedOpen ? <div className="loom-task-advanced-content">{advancedAuthoring}</div> : null}
    </details> : null}
  </aside>;
}
